import { useCallback, useState } from "react";

/**
 * Trigger a browser download for a file record **without navigating the
 * current tab**. The flow is:
 *
 *   1. `fetch()` the `download_url` from the file record (a CloudFront-
 *      presigned URL with `Content-Disposition: attachment` already baked in).
 *   2. Buffer the response into a `Blob`.
 *   3. Click a temporary same-origin `<a download>` pointing at a blob
 *      URL — the browser saves the file silently, the tab stays put.
 *
 * We deliberately avoid anchoring at the presigned URL directly because
 * Chrome ignores the `download` attribute on cross-origin links, which
 * caused the viewer pane to get hijacked into a full-tab navigation.
 *
 * Pass `record.download_url` as the first argument. If it is undefined
 * (e.g. the record is still processing), the function throws so the caller
 * can surface an appropriate error.
 */
export function useFileRecordDownload() {
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(
    async (
      downloadUrl: string | undefined,
      fallbackFileName?: string,
    ): Promise<void> => {
      if (!downloadUrl) {
        throw new Error("No download URL available");
      }
      setIsDownloading(true);
      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(
            `Download failed (${response.status} ${response.statusText})`,
          );
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        try {
          const fileName =
            deriveFilenameFromContentDisposition(
              response.headers.get("content-disposition"),
            ) ||
            fallbackFileName ||
            "download";
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = fileName;
          a.rel = "noopener";
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } finally {
          URL.revokeObjectURL(blobUrl);
        }
      } finally {
        setIsDownloading(false);
      }
    },
    [],
  );

  return { download, isDownloading };
}

function deriveFilenameFromContentDisposition(
  header: string | null,
): string | undefined {
  if (!header) return undefined;
  // Prefer RFC 5987 `filename*` over the plain `filename=` token.
  const encoded = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim();
}
